import { enableSupplierProductLists, isBillingEnabledFor } from '@/config';
import PrivateRoute from '@/modules/auth/components/PrivateRoute';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import BillingDetailsEdit from '@/modules/billing/pages/BillingDetailsEdit';
import BillingHome from '@/modules/billing/pages/BillingHome';
import InvoiceDetails from '@/modules/billing/pages/InvoiceDetails';
import InvoiceList from '@/modules/billing/pages/InvoiceList';
import PaymentMethodSetup from '@/modules/billing/pages/PaymentMethodAdd';
import PaymentMethodList from '@/modules/billing/pages/PaymentMethodList';
import UpgradePlan from '@/modules/billing/pages/UpgradePlan';
import UpgradePlanPaymentSetup from '@/modules/billing/pages/UpgradePlanPaymentSetup';
import {
  BillingDetailsEditPath,
  BillingHomePath,
  BillingInvoiceDetailsPath,
  BillingInvoiceListPath,
  BillingPaymentMethodAddPath,
  BillingPaymentMethodListPath,
  BillingUpgradePlanPath,
  BillingUpgradePlanPaymentSetupPath,
} from '@/modules/billing/paths';
import OrderList from '@/modules/order/pages/OrderList';
import { OrderDetailsPath, OrderListPath } from '@/modules/order/paths';
import {
  ProductAdd,
  ProductCategoryAdd,
  ProductCategoryEdit,
  ProductCategoryList,
  ProductEdit,
  ProductImport,
  ProductList,
  ProductListAdd,
  ProductListEdit,
  ProductListList,
  ProductListPreview,
  ProductListProductAdd,
  ProductListProductList,
  ProductListRetailerList,
  ProductListStoreList,
  PromotionAdd,
  PromotionEdit,
  PromotionList,
} from '@/modules/product/pages';
import {
  ProductAddPath,
  ProductCategoryAddPath,
  ProductCategoryEditPath,
  ProductCategoryListPath,
  ProductCategoryProductListPath,
  ProductEditPath,
  ProductImportPath,
  ProductListAddPath,
  ProductListEditPath,
  ProductListListPath,
  ProductListPath,
  ProductListPreviewPath,
  ProductListProductAddPath,
  ProductListProductListPath,
  ProductListRetailerListPath,
  ProductListStoreListPath,
  PromotionAddPath,
  PromotionEditPath,
  PromotionListPath,
} from '@/modules/product/paths';
import Currency from '@/modules/settings/pages/Currency';
import Language from '@/modules/settings/pages/Language';
import Password from '@/modules/settings/pages/Password';
import { UserRole } from '@/modules/supplier/models/UserRole';
import Attention from '@/modules/supplier/pages/Attention';
import Home from '@/modules/supplier/pages/Home';
import SupplierOrderDetails from '@/modules/supplier/pages/OrderDetails';
import Address from '@/modules/supplier/pages/Settings/Address';
import CustomerAdd from '@/modules/supplier/pages/Settings/CustomerAdd';
import CustomerEdit from '@/modules/supplier/pages/Settings/CustomerEdit';
import CustomerList from '@/modules/supplier/pages/Settings/CustomerList';
import Notification from '@/modules/supplier/pages/Settings/Notification';
import Profile from '@/modules/supplier/pages/Settings/Profile';
import Settings from '@/modules/supplier/pages/Settings/Settings';
import StoreAdd from '@/modules/supplier/pages/Settings/StoreAdd';
import StoreEdit from '@/modules/supplier/pages/Settings/StoreEdit';
import StoreList from '@/modules/supplier/pages/Settings/StoreList';
import {
  CustomerAddPath,
  CustomerEditPath,
  CustomerListPath,
  CustomerStoreAddPath,
  CustomerStoreEditPath,
  CustomerStoreListPath,
  SupplierAttentionPath,
  SupplierHomePath,
  SupplierSettingsPath,
} from '@/modules/supplier/paths';
import { Routes as ReactRoutes, Route } from 'react-router-dom';

const Routes = () => {
  const { role } = useAuth();
  const isSupplierAdmin = role === UserRole.SupplierAdmin;
  const isSupplierBillingEnabled = isBillingEnabledFor('supplier');

  return (
    <ReactRoutes>
      <Route element={<PrivateRoute />}>
        {isSupplierAdmin && (
          <>
            <Route path={SupplierHomePath} Component={Home} />
            <Route path={SupplierAttentionPath} Component={Attention} />
            <Route path={OrderDetailsPath.path} Component={SupplierOrderDetails} />
            <Route path={OrderListPath} Component={OrderList} />
            <Route Component={Settings}>
              <Route path={SupplierSettingsPath.profile} Component={Profile} />
              <Route path={SupplierSettingsPath.address} Component={Address} />
              <Route path={SupplierSettingsPath.businessAddress} Component={Address} />
              <Route path={SupplierSettingsPath.currency} Component={Currency} />
              <Route path={SupplierSettingsPath.language} Component={Language} />
              <Route path={SupplierSettingsPath.notification} Component={Notification} />
              <Route path={SupplierSettingsPath.password} Component={Password} />
              <Route path={CustomerListPath} Component={CustomerList} />
              <Route path={CustomerEditPath.path} Component={CustomerEdit} />
              <Route path={CustomerAddPath} Component={CustomerAdd} />
              <Route path={CustomerStoreListPath.path} Component={StoreList} />
              <Route path={CustomerStoreEditPath.path} Component={StoreEdit} />
              <Route path={CustomerStoreAddPath.path} Component={StoreAdd} />
              <Route path={ProductListPath} Component={ProductList} />
              <Route path={ProductEditPath.path} Component={ProductEdit} />
              <Route path={ProductAddPath} Component={ProductAdd} />
              <Route path={ProductImportPath} Component={ProductImport} />
              <Route path={ProductCategoryListPath} Component={ProductCategoryList} />
              <Route path={ProductCategoryEditPath.path} Component={ProductCategoryEdit} />
              <Route path={ProductCategoryAddPath} Component={ProductCategoryAdd} />
              <Route path={ProductCategoryProductListPath.path} Component={ProductList} />
              {enableSupplierProductLists && (
                <>
                  <Route path={ProductListListPath} Component={ProductListList} />
                  <Route path={ProductListEditPath.path} Component={ProductListEdit} />
                  <Route path={ProductListAddPath} Component={ProductListAdd} />
                  <Route path={ProductListProductAddPath.path} Component={ProductListProductAdd} />
                  <Route
                    path={ProductListProductListPath.path}
                    Component={ProductListProductList}
                  />
                  <Route
                    path={ProductListRetailerListPath.path}
                    Component={ProductListRetailerList}
                  />
                  <Route path={ProductListStoreListPath.path} Component={ProductListStoreList} />
                  <Route path={ProductListPreviewPath.path} Component={ProductListPreview} />
                </>
              )}
              <Route path={PromotionListPath} Component={PromotionList} />
              <Route path={PromotionEditPath.path} Component={PromotionEdit} />
              <Route path={PromotionAddPath} Component={PromotionAdd} />
              {isSupplierBillingEnabled && (
                <>
                  <Route path={BillingHomePath} Component={BillingHome} />
                  <Route path={BillingInvoiceListPath} Component={InvoiceList} />
                  <Route path={BillingInvoiceDetailsPath.path} Component={InvoiceDetails} />
                  <Route path={BillingDetailsEditPath} Component={BillingDetailsEdit} />
                  <Route path={BillingUpgradePlanPath} Component={UpgradePlan} />
                  <Route
                    path={BillingUpgradePlanPaymentSetupPath}
                    Component={UpgradePlanPaymentSetup}
                  />
                  <Route path={BillingPaymentMethodListPath} Component={PaymentMethodList} />
                  <Route path={BillingPaymentMethodAddPath} Component={PaymentMethodSetup} />
                </>
              )}
            </Route>
          </>
        )}
      </Route>
    </ReactRoutes>
  );
};

export default Routes;
